import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  OnInit,
  signal,
} from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { FormControl, FormGroup } from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";

import { filter } from "rxjs";

import { AreaSizeUnitEnum } from "@shared/enums";
import { GeoJSONUtils } from "@shared/utils";
import { CustomValidators } from "@shared/validators";

@Component({
  standalone: false,
  selector: "app-edit-area-dialog",
  templateUrl: "./edit-area-dialog.component.html",
  styleUrl: "./edit-area-dialog.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EditAreaDialogComponent implements OnInit {
  isLoading = signal<boolean>(false);

  private readonly destroyRef = inject(DestroyRef);

  private readonly data: {
    radiusInMeters: number;
  } = inject(MAT_DIALOG_DATA);

  private readonly dialogRef: MatDialogRef<EditAreaDialogComponent> = inject(MatDialogRef);

  formGroup = new FormGroup({
    type: new FormControl(null, [CustomValidators.required]),
    value: new FormControl(null, [CustomValidators.required]),
  });

  public readonly translations: any = {
    dialogContent: $localize`:@@edit-area.specifysize:Specify the size of the area:`,
    unit: $localize`:@@edit-area.selectunit:Select the unit`,
    valueLabel: $localize`:@@edit-area.value:Value`,
    hectaresLabel: $localize`:@@edit-area.hectares:Hectares (area)`,
    sqtKmLabel: $localize`:@@edit-area.squarekm:Square kilometres (area)`,
    kmRadiusLabel: $localize`:@@edit-area.radiuskm:Kilometers (radius)`,
  };

  public readonly areaUnitTypeOptions = [
    { label: this.translations.hectaresLabel, value: AreaSizeUnitEnum.HECTARES },
    { label: this.translations.sqtKmLabel, value: AreaSizeUnitEnum.SQUARE_KILOMETRES },
    { label: this.translations.kmRadiusLabel, value: AreaSizeUnitEnum.KILOMETERS_RADIUS },
  ];

  public hints: { label: string; value: number }[];

  ngOnInit(): void {
    this.formGroup.controls.type.setValue({
      label: this.translations.hectaresLabel,
      value: AreaSizeUnitEnum.HECTARES,
    });
    this.formGroup.valueChanges
      .pipe(
        filter((_) => this.formGroup.valid),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((formValue) => {
        const type = formValue.type.value;
        const area = +formValue.value;

        this.hints = this.generateEquivalencesHints(area, type);
      });
    const area = GeoJSONUtils.calculateCircleAreaForSinglePoint(null, this.data?.radiusInMeters);
    const areaInHa = GeoJSONUtils.getAreaInHa(area);

    this.formGroup.controls.value.setValue(areaInHa ? areaInHa.toFixed(5) : null);
  }

  onSubmit() {
    const selectedType = this.formGroup.controls.type.value.value as AreaSizeUnitEnum;
    const inputValue = +this.formGroup.controls.value.value;

    let radiusInMeters = 0;

    switch (selectedType) {
      case AreaSizeUnitEnum.HECTARES:
        const areaInSqMetersFromHa = inputValue * 10_000;

        radiusInMeters = Math.sqrt(areaInSqMetersFromHa / Math.PI);
        break;

      case AreaSizeUnitEnum.SQUARE_KILOMETRES:
        const areaInSqMetersFromKm2 = inputValue * 1_000_000;

        radiusInMeters = Math.sqrt(areaInSqMetersFromKm2 / Math.PI);
        break;

      case AreaSizeUnitEnum.KILOMETERS_RADIUS:
        radiusInMeters = inputValue * 1000;
        break;

      default:
        radiusInMeters = 0;
        break;
    }

    this.onClose({ value: radiusInMeters });
  }

  onClose(result: { value: number } = null) {
    this.dialogRef.close(result);
  }

  isSubmitButtonDisabled() {
    return this.formGroup.invalid;
  }

  public generateEquivalencesHints(
    value: number,
    unit: AreaSizeUnitEnum,
  ): { label: string; value: number }[] {
    const formatToFiveDecimals = (num: number): number => parseFloat(num.toFixed(5));

    let hectares = 0;
    let squareKilometres = 0;
    let radiusKm = 0;

    switch (unit) {
      case AreaSizeUnitEnum.HECTARES:
        hectares = value;
        squareKilometres = value * 0.01;
        radiusKm = Math.sqrt((value * 10_000) / Math.PI) / 1000;
        break;

      case AreaSizeUnitEnum.SQUARE_KILOMETRES:
        squareKilometres = value;
        hectares = value * 100;
        radiusKm = Math.sqrt(value / Math.PI);
        break;

      case AreaSizeUnitEnum.KILOMETERS_RADIUS:
        radiusKm = value;
        const areaInSquareMeters = Math.PI * Math.pow(value * 1000, 2);

        squareKilometres = areaInSquareMeters / 1_000_000;
        hectares = squareKilometres * 100;
        break;
    }

    return [
      {
        label: this.translations.hectaresLabel,
        value: formatToFiveDecimals(hectares),
      },
      {
        label: this.translations.sqtKmLabel,
        value: formatToFiveDecimals(squareKilometres),
      },
      {
        label: this.translations.kmRadiusLabel,
        value: formatToFiveDecimals(radiusKm),
      },
    ];
  }
}
